import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import MgmtLayout from '@/components/feature/MgmtLayout';
import { useManagementProjectsLite } from '@/hooks/useManagementProjectsLite';
import { useAuth } from '@/hooks/useAuth';
import { getProjectCoverImage } from '@/lib/projectCover';

const statusColor: Record<string, string> = {
  active: '#059669',
  planning: '#F59E0B',
  on_hold: '#EF4444',
  complete: '#0891B2',
  completed: '#0891B2',
};

const statusBg: Record<string, string> = {
  active: '#F0FDF4',
  planning: '#FFFBEB',
  on_hold: '#FEF2F2',
  complete: '#ECFEFF',
  completed: '#ECFEFF',
};

function getCoverImage(project: { project_type: string; cover_image: string | null }): string {
  return getProjectCoverImage(project);
}

export default function ManagementDashboard() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const {
    projects,
    loading,
    error,
    refresh,
  } = useManagementProjectsLite();

  const activeProjects = useMemo(() => projects.filter((p) => p.calculatedStatus === 'active'), [projects]);
  const onHoldProjects = useMemo(() => projects.filter((p) => p.calculatedStatus === 'on_hold'), [projects]);
  const planningProjects = useMemo(() => projects.filter((p) => p.calculatedStatus === 'planning'), [projects]);
  const completeProjects = useMemo(() => projects.filter((p) => p.calculatedStatus === 'complete'), [projects]);

  const { profile } = useAuth();

  const [slowLoadWarning, setSlowLoadWarning] = useState(false);

  // Non-blocking slow-load hint — does not stop loading or force empty state.
  useEffect(() => {
    if (!loading || projects.length > 0 || error) {
      setSlowLoadWarning(false);
      return;
    }
    const timer = setTimeout(() => {
      if (loading && projects.length === 0 && !error) {
        setSlowLoadWarning(true);
        console.warn('[Dashboard] Projects still loading after 8s', { profileId: profile?.id, role: profile?.user_type });
      }
    }, 8000);
    return () => clearTimeout(timer);
  }, [loading, projects.length, error, profile?.id, profile?.user_type]);

  const statusLabel: Record<string, string> = {
    active: t('statusLabel.active'),
    planning: t('statusLabel.planning'),
    on_hold: t('statusLabel.on_hold'),
    complete: t('statusLabel.complete'),
    completed: t('statusLabel.complete'),
  };

  const handleRetry = () => {
    setSlowLoadWarning(false);
    refresh();
  };

  const SlowLoadBanner = () => (
    <div className="mb-4 bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
      <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
        <i className="ri-time-line text-amber-600 text-sm"></i>
      </div>
      <div className="flex-1">
        <p className="text-amber-800 text-sm font-semibold">{t('dashboard.loadingSlow', 'Projects are taking longer than expected. Please retry.')}</p>
        <button onClick={handleRetry} className="text-amber-700 text-xs font-medium mt-1 hover:underline cursor-pointer">
          {t('btn.retry') || 'Retry'}
        </button>
      </div>
    </div>
  );

  const ErrorBanner = () => (
    <div className="mb-4 bg-white border border-red-100 rounded-2xl p-4 flex items-start gap-3">
      <div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center flex-shrink-0">
        <i className="ri-error-warning-line text-red-500 text-sm"></i>
      </div>
      <div className="flex-1">
        <p className="text-[#374151] text-sm font-semibold">{error}</p>
        <button onClick={handleRetry} className="text-[#E8621A] text-xs font-medium mt-1 cursor-pointer hover:underline">
          {t('btn.retry') || 'Retry'}
        </button>
      </div>
    </div>
  );

  // ─── Skeleton while the first fetch is in flight ───
  if (loading && projects.length === 0 && !error) {
    return (
      <MgmtLayout>
        <div className="px-4 lg:px-8 pt-6 pb-8 bg-[#F8FAFC] min-h-screen">
          {slowLoadWarning && <SlowLoadBanner />}
          <div className="flex items-start justify-between mb-6">
            <div className="space-y-2">
              <div className="h-3 bg-gray-200 rounded animate-pulse w-24" />
              <div className="h-7 bg-gray-200 rounded animate-pulse w-40" />
            </div>
            <div className="h-10 bg-gray-200 rounded-xl animate-pulse w-32" />
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            {[0,1,2,3].map((i) => (
              <div key={i} className="bg-white rounded-2xl p-4 border border-gray-100">
                <div className="h-8 bg-gray-100 rounded animate-pulse w-12 mb-2" />
                <div className="h-4 bg-gray-100 rounded animate-pulse w-24" />
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {[0,1].map((i) => (
              <div key={i} className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                <div className="h-48 bg-gray-100 animate-pulse" />
                <div className="p-4 space-y-2">
                  <div className="h-4 bg-gray-100 rounded animate-pulse w-3/4" />
                  <div className="h-2 bg-gray-100 rounded-full animate-pulse" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </MgmtLayout>
    );
  }

  // ─── Project card ───
  const ProjectCard = ({ project }: { project: (typeof projects)[0] }) => {
    const progress = project.calculatedProgress;
    const currentStage = project.calculatedStage;
    const coverImage = getCoverImage(project);
    const color = statusColor[project.calculatedStatus] || '#9CA3AF';
    const bg = statusBg[project.calculatedStatus] || '#F9FAFB';
    const label = statusLabel[project.calculatedStatus] || project.calculatedStatus;

    return (
      <button
        onClick={() => navigate(`/management/projects/${project.id}`)}
        className="bg-white rounded-2xl border border-gray-100 overflow-hidden text-left hover:border-gray-200 hover:-translate-y-0.5 transition-all duration-200 cursor-pointer group"
      >
        <div className="h-44 relative overflow-hidden">
          <img src={coverImage} alt={project.project_name} className="w-full h-full object-cover object-top group-hover:scale-[1.02] transition-transform duration-300" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A]/80 via-[#1A1A1A]/10 to-transparent"></div>
          <div className="absolute top-3 left-3">
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-black/30 text-white backdrop-blur-sm border border-white/10">
              {project.project_code}
            </span>
          </div>
          <div className="absolute top-3 right-3">
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: bg, color }}>
              {label}
            </span>
          </div>
          <div className="absolute bottom-3 left-3 right-3">
            <div className="text-white font-bold text-sm leading-tight">{project.project_name}</div>
            <div className="text-white/60 text-[11px] mt-0.5 truncate">{project.site_address || t('projects.noAddress')}</div>
          </div>
        </div>
        <div className="px-4 py-3 space-y-2">
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[#6B7280] text-xs font-medium">{currentStage}</span>
              <span className="text-sm font-bold" style={{ color: progress === 100 ? '#059669' : '#E8621A' }}>{progress}%</span>
            </div>
            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div className="h-full rounded-full transition-all" style={{ width: `${progress}%`, backgroundColor: progress === 100 ? '#059669' : '#E8621A' }}></div>
            </div>
          </div>
          {project.client_name && (
            <div className="flex items-center gap-1.5">
              <i className="ri-user-line text-[10px] text-[#9CA3AF]"></i>
              <span className="text-[#9CA3AF] text-xs truncate">{project.client_name}</span>
            </div>
          )}
        </div>
      </button>
    );
  };

  // ─── Compact list item ───
  const CompactItem = ({ project, badgeColor, badgeBg }: { project: (typeof projects)[0]; badgeColor: string; badgeBg: string }) => (
    <button
      onClick={() => navigate(`/management/projects/${project.id}`)}
      className="w-full bg-white rounded-xl border border-gray-100 p-3.5 text-left flex items-center gap-3 cursor-pointer hover:border-gray-200 transition-colors"
    >
      <div className="w-12 h-12 rounded-xl overflow-hidden flex-shrink-0">
        <img src={getCoverImage(project)} alt={project.project_name} className="w-full h-full object-cover object-center" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[#1A1A1A] text-sm font-semibold truncate">{project.project_name}</div>
        <div className="text-[#9CA3AF] text-xs mt-0.5 truncate">{project.site_address || t('projects.noAddressShort')}</div>
        <div className="flex items-center gap-2 mt-1 flex-wrap">
          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full" style={{ backgroundColor: badgeBg, color: badgeColor }}>
            {project.calculatedStage}
          </span>
          <span className="text-[10px] text-[#9CA3AF]">{project.calculatedProgress}%</span>
        </div>
      </div>
      <i className="ri-arrow-right-s-line text-[#D1D5DB] flex-shrink-0"></i>
    </button>
  );

  return (
    <MgmtLayout>
      <div className="w-full max-w-full overflow-x-hidden bg-[#F8FAFC] min-h-screen">
        {(slowLoadWarning && loading && projects.length === 0 && !error) && (
          <div className="px-4 lg:px-8 pt-4">
            <SlowLoadBanner />
          </div>
        )}

        {error && projects.length === 0 && (
          <div className="px-4 lg:px-8 pt-4">
            <ErrorBanner />
          </div>
        )}

        {/* ═══════ MOBILE ═══════ */}
        <div className="lg:hidden px-4 pt-5 pb-28 overflow-x-hidden">
          <div className="flex items-center justify-between mb-5">
            <div>
              <p className="text-[#9CA3AF] text-xs font-semibold uppercase tracking-wider">{t('nav.management')}</p>
              <h1 className="text-xl font-bold text-[#1A1A1A] mt-0.5">{t('dashboard.projectControl')}</h1>
            </div>
            <button
              onClick={() => navigate('/management/projects/new')}
              className="flex items-center gap-1.5 bg-[#E8621A] text-white px-3.5 py-2 rounded-xl text-sm font-semibold cursor-pointer whitespace-normal sm:whitespace-nowrap"
            >
              <i className="ri-add-line text-base"></i>
              {t('btn.new')}
            </button>
          </div>

          {/* Quick stats */}
          <div className="grid grid-cols-2 gap-2.5 mb-5">
            {[
              { value: activeProjects.length, label: t('dashboard.activeProjects'), icon: 'ri-building-2-line', color: '#E8621A', bg: '#FEF0E7' },
              { value: completeProjects.length, label: t('dashboard.completed'), icon: 'ri-checkbox-circle-line', color: '#059669', bg: '#F0FDF4' },
              { value: onHoldProjects.length, label: t('dashboard.onHold'), icon: 'ri-pause-circle-line', color: '#EF4444', bg: '#FEF2F2' },
              { value: planningProjects.length, label: t('dashboard.planning'), icon: 'ri-draft-line', color: '#F59E0B', bg: '#FFFBEB' },
            ].map((s) => (
              <div key={s.label} className="bg-white rounded-2xl p-3.5 flex items-center gap-3 border border-gray-100">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: s.bg }}>
                  <i className={`${s.icon} text-base`} style={{ color: s.color }}></i>
                </div>
                <div>
                  <div className="text-xl font-bold leading-none" style={{ color: s.color }}>{s.value}</div>
                  <div className="text-[#6B7280] text-xs mt-0.5">{s.label}</div>
                </div>
              </div>
            ))}
          </div>

          {error && projects.length === 0 && <ErrorBanner />}

          {activeProjects.length > 0 && (
            <div className="mb-5">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-bold text-[#1A1A1A]">{t('dashboard.activeProjects')}</h2>
                <button onClick={() => navigate('/management/projects')} className="text-[#E8621A] text-xs font-semibold cursor-pointer whitespace-nowrap">{t('dashboard.viewAll')}</button>
              </div>
              <div className="space-y-3">
                {activeProjects.map((p) => <ProjectCard key={p.id} project={p} />)}
              </div>
            </div>
          )}

          {completeProjects.length > 0 && (
            <div className="mb-5">
              <h2 className="text-sm font-bold text-[#1A1A1A] mb-3">{t('dashboard.completed')}</h2>
              <div className="space-y-2">
                {completeProjects.map((p) => <CompactItem key={p.id} project={p} badgeColor="#0891B2" badgeBg="#ECFEFF" />)}
              </div>
            </div>
          )}

          {onHoldProjects.length > 0 && (
            <div className="mb-5">
              <h2 className="text-sm font-bold text-[#1A1A1A] mb-3">{t('dashboard.onHold')}</h2>
              <div className="space-y-2">
                {onHoldProjects.map((p) => <CompactItem key={p.id} project={p} badgeColor="#EF4444" badgeBg="#FEF2F2" />)}
              </div>
            </div>
          )}

          {planningProjects.length > 0 && (
            <div className="mb-5">
              <h2 className="text-sm font-bold text-[#1A1A1A] mb-3">{t('dashboard.planning')}</h2>
              <div className="space-y-2">
                {planningProjects.map((p) => <CompactItem key={p.id} project={p} badgeColor="#F59E0B" badgeBg="#FFFBEB" />)}
              </div>
            </div>
          )}

          {activeProjects.length === 0 && completeProjects.length === 0 && onHoldProjects.length === 0 && planningProjects.length === 0 && projects.length > 0 && (
            <div className="mb-5">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-bold text-[#1A1A1A]">{t('dashboard.allProjects') || 'All Projects'}</h2>
                <button onClick={() => navigate('/management/projects')} className="text-[#E8621A] text-xs font-semibold cursor-pointer whitespace-nowrap hover:underline">
                  {t('dashboard.manage') || 'Manage'}
                </button>
              </div>
              <div className="space-y-3">
                {projects.map((p) => <ProjectCard key={p.id} project={p} />)}
              </div>
            </div>
          )}

          {projects.length === 0 && !loading && !error && (
            <div className="text-center py-12">
              <div className="w-12 h-12 rounded-2xl bg-white border border-gray-100 flex items-center justify-center mx-auto mb-3">
                <i className="ri-building-2-line text-2xl text-[#D1D5DB]"></i>
              </div>
              <p className="text-[#374151] font-semibold text-sm">{t('dashboard.noActiveProjects')}</p>
              <div className="flex items-center justify-center gap-3 mt-3">
                <button onClick={handleRetry} className="text-[#E8621A] text-sm font-medium cursor-pointer hover:underline">
                  {t('btn.retry') || 'Retry'}
                </button>
                <span className="text-[#D1D5DB]">·</span>
                <button onClick={() => navigate('/management/projects/new')} className="text-[#E8621A] text-sm font-medium cursor-pointer hover:underline">
                  {t('dashboard.createProject')}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ═══════ DESKTOP ═══════ */}
        <div className="hidden lg:block px-8 pt-6 pb-8 overflow-x-hidden">
          {/* Header */}
          <div className="flex items-start justify-between mb-6">
            <div>
              <p className="text-[#9CA3AF] text-xs font-semibold uppercase tracking-wider">{t('dashboard.subtitle')}</p>
              <h1 className="text-2xl font-bold text-[#1A1A1A] mt-1">{t('dashboard.title')}</h1>
              <p className="text-[#9CA3AF] text-sm mt-0.5">
                {activeProjects.length} {t('dashboard.activeProjects').toLowerCase()} · {completeProjects.length} {t('dashboard.completed').toLowerCase()} · {onHoldProjects.length} {t('dashboard.onHold').toLowerCase()} · {planningProjects.length} {t('dashboard.planning').toLowerCase()}
              </p>
            </div>
            <button
              onClick={() => navigate('/management/projects/new')}
              className="flex items-center gap-2 bg-[#E8621A] text-white px-4 py-2.5 rounded-xl text-sm font-semibold cursor-pointer hover:bg-[#B84C10] transition-colors whitespace-normal sm:whitespace-nowrap"
            >
              <i className="ri-add-line text-base"></i>
              {t('btn.newProject')}
            </button>
          </div>

          {error && projects.length === 0 && <ErrorBanner />}

          {/* Stats row */}
          <div className="grid grid-cols-4 gap-3 mb-6">
            {[
              { value: activeProjects.length, label: t('dashboard.activeProjects'), sub: t('dashboard.inProgressNow'), color: '#E8621A', bg: '#FEF0E7', icon: 'ri-building-2-line' },
              { value: completeProjects.length, label: t('dashboard.completed'), sub: t('dashboard.fullyFinished'), color: '#059669', bg: '#F0FDF4', icon: 'ri-checkbox-circle-line' },
              { value: onHoldProjects.length, label: t('dashboard.onHold'), sub: t('dashboard.blockedProjects'), color: '#EF4444', bg: '#FEF2F2', icon: 'ri-pause-circle-line' },
              { value: planningProjects.length, label: t('dashboard.planning'), sub: t('dashboard.notYetStarted'), color: '#F59E0B', bg: '#FFFBEB', icon: 'ri-draft-line' },
            ].map((stat) => (
              <div key={stat.label} className="bg-white rounded-2xl p-4 border border-gray-100 flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: stat.bg }}>
                  <i className={`${stat.icon} text-lg`} style={{ color: stat.color }}></i>
                </div>
                <div>
                  <div className="text-2xl font-bold" style={{ color: stat.color }}>{stat.value}</div>
                  <div className="text-[#374151] text-sm font-semibold leading-tight">{stat.label}</div>
                  <div className="text-[#9CA3AF] text-xs mt-0.5">{stat.sub}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="flex gap-5">
            {/* LEFT — grouped projects */}
            <div className="flex-1 min-w-0">
              {activeProjects.length > 0 && (
                <>
                  <div className="flex items-center justify-between mb-3">
                    <h2 className="text-sm font-bold text-[#1A1A1A]">{t('dashboard.activeProjects')}</h2>
                    <button onClick={() => navigate('/management/projects')} className="text-[#E8621A] text-xs font-semibold cursor-pointer whitespace-nowrap hover:underline">
                      {t('dashboard.viewAll')}
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-3 mb-5">
                    {activeProjects.map((p) => <ProjectCard key={p.id} project={p} />)}
                  </div>
                </>
              )}

              {completeProjects.length > 0 && (
                <div className="mb-5">
                  <h2 className="text-sm font-bold text-[#1A1A1A] mb-3">{t('dashboard.completed')}</h2>
                  <div className="space-y-2">
                    {completeProjects.map((p) => <CompactItem key={p.id} project={p} badgeColor="#0891B2" badgeBg="#ECFEFF" />)}
                  </div>
                </div>
              )}

              {onHoldProjects.length > 0 && (
                <div className="mb-5">
                  <h2 className="text-sm font-bold text-[#1A1A1A] mb-3">{t('dashboard.onHold')}</h2>
                  <div className="space-y-2">
                    {onHoldProjects.map((p) => <CompactItem key={p.id} project={p} badgeColor="#EF4444" badgeBg="#FEF2F2" />)}
                  </div>
                </div>
              )}

              {planningProjects.length > 0 && (
                <div className="mb-5">
                  <h2 className="text-sm font-bold text-[#1A1A1A] mb-3">{t('dashboard.planning')}</h2>
                  <div className="space-y-2">
                    {planningProjects.map((p) => <CompactItem key={p.id} project={p} badgeColor="#F59E0B" badgeBg="#FFFBEB" />)}
                  </div>
                </div>
              )}

              {activeProjects.length === 0 && completeProjects.length === 0 && onHoldProjects.length === 0 && planningProjects.length === 0 && projects.length > 0 && (
                <div className="mb-5">
                  <div className="flex items-center justify-between mb-3">
                    <h2 className="text-sm font-bold text-[#1A1A1A]">{t('dashboard.allProjects') || 'All Projects'}</h2>
                    <button onClick={() => navigate('/management/projects')} className="text-[#E8621A] text-xs font-semibold cursor-pointer whitespace-nowrap hover:underline">
                      {t('dashboard.manage') || 'Manage'}
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {projects.map((p) => <ProjectCard key={p.id} project={p} />)}
                  </div>
                </div>
              )}

              {projects.length === 0 && !loading && !error && (
                <div className="text-center py-12">
                  <div className="w-12 h-12 rounded-2xl bg-white border border-gray-100 flex items-center justify-center mx-auto mb-3">
                    <i className="ri-building-2-line text-2xl text-[#D1D5DB]"></i>
                  </div>
                  <p className="text-[#374151] font-semibold text-sm">{t('dashboard.noActiveProjects')}</p>
                  <div className="flex items-center justify-center gap-3 mt-3">
                    <button onClick={handleRetry} className="text-[#E8621A] text-sm font-medium cursor-pointer hover:underline">
                      {t('btn.retry') || 'Retry'}
                    </button>
                    <span className="text-[#D1D5DB]">·</span>
                    <button onClick={() => navigate('/management/projects/new')} className="text-[#E8621A] text-sm font-medium cursor-pointer hover:underline">
                      {t('dashboard.createProject')}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* RIGHT — all projects list */}
            <div className="w-72 flex-shrink-0 space-y-4">
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-sm font-bold text-[#1A1A1A]">{t('dashboard.allProjects')}</h2>
                  <button onClick={() => navigate('/management/projects')} className="text-[#E8621A] text-xs font-semibold cursor-pointer whitespace-nowrap hover:underline">
                    {t('dashboard.manage')}
                  </button>
                </div>
                <div className="bg-white rounded-2xl border border-gray-100 divide-y divide-gray-50 overflow-hidden">
                  {projects.slice(0, 8).map((project) => {
                    const color = statusColor[project.calculatedStatus] || '#9CA3AF';
                    const label = statusLabel[project.calculatedStatus] || project.calculatedStatus;
                    const progress = project.calculatedProgress;
                    return (
                      <button
                        key={project.id}
                        onClick={() => navigate(`/management/projects/${project.id}`)}
                        className="w-full flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-[#F8FAFC] transition-colors text-left"
                      >
                        <div className="w-8 h-8 rounded-lg overflow-hidden flex-shrink-0">
                          <img src={getCoverImage(project)} alt={project.project_name} className="w-full h-full object-cover object-center" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-[#1A1A1A] text-sm font-semibold truncate">{project.project_name}</div>
                          <div className="flex items-center gap-1.5 mt-0.5">
                            <span className="text-[10px] font-bold" style={{ color }}>{label}</span>
                            <span className="text-[#9CA3AF] text-[10px]">· {progress}%</span>
                          </div>
                        </div>
                        <i className="ri-arrow-right-s-line text-[#D1D5DB] text-sm"></i>
                      </button>
                    );
                  })}
                  {projects.length > 8 && (
                    <button
                      onClick={() => navigate('/management/projects')}
                      className="w-full px-4 py-3 text-[#E8621A] text-xs font-semibold cursor-pointer hover:bg-[#F8FAFC] transition-colors text-center"
                    >
                      +{projects.length - 8} {t('projects.projects').toLowerCase()}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </MgmtLayout>
  );
}